import { getQueryParameter } from '@/services/getQueryParameter'
import { SsoConfig } from '@/types/interfaces'
import { Ref, ref } from 'vue'
import config from '../config/index.json'

export const DEFAULT_CONFIG: Ref<SsoConfig> = ref({
  password_policy: {
    minimum_length: 10,
    require_lowercase: true,
    require_numbers: true,
    require_symbols: false,
    require_uppercase: false,
    temporary_password_validity_days: 7
  },
  sso: {
    sso_api_and_ui_domain: '',
    lms_url: '',
    dev_env: false,
    learn_more_hyperlink: '',
    get_started_hyperlink: ''
  },
  connections: {
    get_started_hyperlink: '',
    forgot_password_hyperlink: ''
  },
  sp: {
    get_started_hyperlink: '',
    forgot_password_hyperlink: ''
  },
  api: {
    url: '',
    service_path: '',
    failover_service_path: '',
    lms_redirect_url: '',
    connections_redirect_url: '',
    connections_login_url: '',
    secure_payments_redirect_url: '',
    secure_payments_login_url: '',
    secure_payments_sign_on_url: '',
    google_client_id: '',
    hide_login_form: false
  },
  saml: {
    providerName: '',
    providerLogo: '',
    samlURL: '',
    loginActive: false,
    providerPalette: {
      color: '#fff',
      background: {
        main: '#1a62ff',
        hover: '#8cb1ff'
      },
      border: '1px solid #d3dae4'
    }
  },
  login_ui_methods: {
    username_password_login: true,
    google_login: true,
    forgot_password: true
  }
})

let cachedConfig: SsoConfig

/**
 * Deprecated. Please use useConfigSync instead
 */
export const useConfig = async (): Promise<SsoConfig> => {
  if (!cachedConfig) {
    await loadConfig()
  }
  return cachedConfig
}

/**
 * The same as useConfig but Synchronously
 */
export const useConfigSync = (): SsoConfig => {
  return cachedConfig
}

/**
 * This method is used when the app is loading to have available the config
 * before any component gets rendered. Some components may need the config
 * before getting rendered.
 */
export const loadConfig = async () => {
  const stage = getQueryParameter('stage')

  try {
    const configFile = await getConfigFile()
    const configJson = await configFile.json()
    configJson.api.url = configJson.api.url.indexOf('https://') === 0 ? configJson.api.url : `https://${configJson.api.url}`
    cachedConfig = configJson
  } catch (e) {
    console.error('Error loading config file')
    cachedConfig = config
  }

  if (stage) {
    cachedConfig.api.url = `${cachedConfig.api.url}/${stage}`
  }
}

const getConfigFile = async () => {
  const perHostConfig = await fetch(`/config/index-${btoa(window.location.host)}.json?v=20241017`)
  const isJsonResponse = perHostConfig.headers.get('content-type')?.includes('application/json')

  let s3Config: any
  if (perHostConfig.status === 200 && isJsonResponse) {
    s3Config = await perHostConfig.json()
  } else {
    const defaultConfig = await fetch('/config/index.json?v=20241017')
    s3Config = await defaultConfig.json()
  }

  try {
    const apiUrl = window.location.origin
    const dbConfigUrl = `${apiUrl}/api/v1/uiConfig?domain=${encodeURIComponent(window.location.host)}`
    const dbConfigResponse = await fetch(dbConfigUrl)
    
    if (dbConfigResponse.status === 200 && dbConfigResponse.headers.get('content-type')?.includes('application/json')) {
      const dbConfig = await dbConfigResponse.json()
      const mergedConfig = mergeLoginConfig(s3Config, dbConfig)
      return { json: async () => mergedConfig }
    }
  } catch (error) {
    console.log('No database config found, using S3 config')
  }

  return { json: async () => s3Config }
}

const mergeLoginConfig = (s3Config: any, dbConfig: any) => {
  if (!dbConfig) return s3Config
  
  const samlConfig = dbConfig.saml_login_config || {}
  
  return {
    ...s3Config,
    login_ui_methods: {
      ...s3Config.login_ui_methods,
      username_password_login: dbConfig.username_password_login ?? s3Config.login_ui_methods?.username_password_login,
      google_login: dbConfig.google_login ?? s3Config.login_ui_methods?.google_login,
      forgot_password: dbConfig.forgot_password ?? s3Config.login_ui_methods?.forgot_password
    },
    saml: {
      ...s3Config.saml,
      loginActive: dbConfig.saml_login ?? s3Config.saml?.loginActive,
      providerLogo: samlConfig.logo || s3Config.saml?.providerLogo,
      providerPalette: {
        ...s3Config.saml?.providerPalette,
        color: samlConfig.color || s3Config.saml?.providerPalette?.color,
        background: {
          ...s3Config.saml?.providerPalette?.background,
          main: samlConfig.background_main_color || s3Config.saml?.providerPalette?.background?.main,
          hover: samlConfig.background_hover_color || s3Config.saml?.providerPalette?.background?.hover
        },
        border: samlConfig.border_color || s3Config.saml?.providerPalette?.border
      }
    }
  }
}
